/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useMemo } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { formUsersUrl } from "@Services/dataManager";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import SlickGrid1, { typedColumns, type GridRowBase, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormFlowUser } from "@ServerTypes/forms/forms.schema";
import { GetUsersListRequest } from "@ServerTypes/accounts/accounts.schema";
interface UserRow extends GridRowBase {
  id?: ObjectIdString;
  fullName?: string;
  email?: string;
  organisation?: { name?: string };
  designation?: string;
}

type User = Partial<Pick<FormFlowUser, "id">> & {
  fullName?: FormFlowUser["name"];
};

type FormUsersQuery = Partial<
  Pick<NonNullable<GetUsersListRequest["value"]>, "projectId" | "sectionId" | "selectionScope" | "formId" | "spaceId">
>;

/** The scopes the users-list endpoint accepts, taken from its own enum so the two cannot drift. */
type UserSelectionScope = NonNullable<FormUsersQuery["selectionScope"]>;
const USER_SELECTION_SCOPES: readonly UserSelectionScope[] = ["currentSection", "entireForm", "space", "project"];

/**
 * Narrow a question's `selectionScope` to a scope this endpoint understands.
 *
 * The stored key is polymorphic — a date-time question writes "anyDateTime" / "rangeBased" / … to
 * the same field — so a plain widening would send the server a value its `z.enum` rejects. Matching
 * against the list (rather than asserting with `as`) makes an unrecognised scope come back
 * `undefined`, which is what the field-less callers already send.
 */
const toUserSelectionScope = (scope?: string): UserSelectionScope | undefined =>
  USER_SELECTION_SCOPES.find((allowed) => allowed === scope);
/**
 * The `users` question this modal is picking for. Neither key is modelled by `questionFieldSchema`
 * — its `.catchall(z.unknown())` would type both `unknown`, which no comparison accepts — so both
 * are declared here and reported as a server-schema gap. Both are OPTIONAL: they are written by the
 * form-template builder (portal-settings FormsAndFields) only for user-type questions, and this
 * modal already reads each through `?.` with a fallback.
 *
 * `selectionScope` stays a plain `string` rather than the four user scopes ("project" | "space" |
 * "entireForm" | "currentSection"). The key is SHARED with date-time questions, where the same
 * builder writes "anyDateTime" | "rangeBased" | "currentDateTime" | "futureDateTime" |
 * "pastDateTime" (valueContentForSideBar.tsx) onto the same question-field object. Narrowing it to
 * the user scopes asserts something the stored field does not guarantee.
 */
interface UsersFieldData {
  selectionType?: "singleUser" | "multipleUser";
  selectionScope?: string;
}

interface AddUserProps {
  isVisible?: boolean;
  closeUsersModal: () => void;
  handleAddUserClick: (args: {
    handleCustomFieldData?: unknown;
    preSelectedUsers: User[];
    allSelectedUsers: User[];
  }) => void;
  /** Already-assigned users: seeds the selection and is disabled in the grid. */
  currentUsers?: FormFlowUser[];
  section?: { _id?: string };
  fieldData?: UsersFieldData;
  spaceId?: ObjectIdString;
  formId?: ObjectIdString;
  projectId?: ObjectIdString;
  customQuery?: FormUsersQuery;
  handleCustomFieldData?: unknown;
}

const AddUser = ({
  isVisible = true,
  closeUsersModal,
  handleAddUserClick,
  currentUsers = [],
  section,
  fieldData,
  spaceId,
  formId,
  projectId,
  customQuery,
  handleCustomFieldData,
}: AddUserProps) => {
  const { t } = useTranslation();
  const [disableAddBtn, setDisableAddBtn] = useState(true);
  const dispatch = useDispatch();
  const [allSelectedUsers, setAllSelectedUsers] = useState<User[]>([]);
  const selectionMode = fieldData?.selectionType || "multipleUser";
  const dataGrid = useRef<SlickGrid1Ref<UserRow> | null>(null);

  const headerContent = () => (
    <div className=" w-full flex justify-between rounded-md">
      <div className="">{t("Add Users")}</div>
    </div>
  );

  useEffect(() => {
    const data = currentUsers.map((user) => ({ id: user.id, fullName: user.name }));
    setAllSelectedUsers(data);
  }, []);

  const footerContent = () => (
    <div className=" flex gap-4">
      <ButtonComponent
        disabled={disableAddBtn}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={() => {
          handleAddUserClick({
            handleCustomFieldData,
            preSelectedUsers: allSelectedUsers, // patch
            allSelectedUsers,
          });
          closeUsersModal();
        }}
      >
        {t("Add")}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeUsersModal}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  useEffect(() => {
    setDisableAddBtn(allSelectedUsers.length == 0);
  }, [allSelectedUsers.length]);

  const handleRemoveUser = (userId: User["id"]) => {
    setAllSelectedUsers((prev) => prev.filter((user) => user.id != userId));
  };

  const nameTemplate = (
    _row: number,
    _cell: number,
    _value: string | undefined,
    _columnDef: unknown,
    dataContext: UserRow,
  ) => {
    const { id } = dataContext;
    const container = document.createElement("div");
    renderUserTag(container, { id: id });
    return container;
  };

  const handleAddUserToSelection = () => {
    const selectedUsers = dataGrid.current?.dataView?.getAllSelectedItems?.() || [];
    let data = [...allSelectedUsers, ...selectedUsers];
    // `as const` makes each entry the [key, value] tuple `new Map` needs; it is erased at emit.
    let uniqueData = [...new Map(data.map((item) => [item.id, item] as const)).values()];
    if (selectionMode == "singleUser" && uniqueData.length > 1) {
      // Non-null: `length > 0` guarantees index 0 exists, which `noUncheckedIndexedAccess` cannot
      // see. Erased at emit.
      uniqueData = uniqueData.length > 0 ? [uniqueData[0]!] : [];
      dispatch(
        showToastNotification({
          content: t("Only single user can be added"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
    setAllSelectedUsers(() => uniqueData);
  };

  const queryObj: FormUsersQuery = customQuery ?? {
    projectId,
    sectionId: section?._id,
    selectionScope: toUserSelectionScope(fieldData?.selectionScope),
    formId,
  };
  if (spaceId) {
    queryObj.spaceId = spaceId;
  }

  const [query] = useState<FormUsersQuery>(queryObj);
  const columnDefinitions = useMemo(
    () =>
      typedColumns<UserRow>([
        {
          field: "fullName",
          id: "fullName",
          filterType: "Text",
          name: t("User Name"),
          width: 150,
          formatter: nameTemplate,
        },
        {
          field: "email",
          id: "email",
          filterType: "Text",
          name: t("E-Mail ID"),
          width: 150,
        },
        {
          field: "organisation",
          id: "organisation.name",
          filterType: "Text",
          name: t("Org"),
          width: 150,
          _valueGetter: (row) => row.organisation?.name,
        },
        {
          field: "designation",
          id: "designation",
          filterType: "Text",
          name: t("Designation"),
          width: 150,
        },
      ]),
    [],
  );
  const getUsersGrid = () => (
    <SlickGrid1
      ref={dataGrid}
      remoteDataSource={formUsersUrl}
      query={query}
      infiniteScroll
      enableFilteringOption
      disabledIds={currentUsers.map((u) => u.id)}
      enableSortingOption
      columnDefinitions={columnDefinitions}
    />
  );
  const modelContent = () => (
    <div className="w-full h-full overflow-auto gap-5 flex flex-col">
      <div className="flex flex-2 flex-col overflow-auto border border-button_light rounded-lg">
        <div className="border border-button_light" />
        {getUsersGrid()}
        <div className="p-4">
          <ButtonComponent onClick={handleAddUserToSelection}>{t("Add to selection")}</ButtonComponent>
        </div>
      </div>
      <div className="border flex-1 overflow-auto flex flex-col border-button_light rounded-lg">
        <div className="p-4 font-semibold">{t("Selection")}</div>
        <div className="border border-button_light" />
        <div className="flex p-4 overflow-auto flex-wrap gap-3">
          {allSelectedUsers.map((user) => (
            <UserNameTagComponent
              key={user.id}
              handleDeleteIconClicked={() => {
                handleRemoveUser(user.id);
              }}
              id={user.id}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const getUserModal = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={footerContent()}
      width="70%"
      height="100%"
      headerTitle={headerContent()}
      onCancel={closeUsersModal}
      isVisible={isVisible}
      modelContent={modelContent()}
    />
  );

  return getUserModal();
};
export default AddUser;
